import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  Grid,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Edit, PersonOff, PersonAdd } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { apiUrl } from "./LoginSignup";
import AddUser from "./AddUser";
import { requestJson } from "../utils/requestJson";
import {
  FEATURE_KEYS,
  FEATURE_LABELS,
  ROLE_TEMPLATE_OPTIONS,
  applyRoleTemplate,
  getDefaultFeaturePermissionsForRole,
} from "../utils/featureAccess";

const AVATAR_CACHE_MS = 5 * 60 * 1000;
const avatarCache = new Map();

const UserAvatar = ({ user, token, size = 34 }) => {
  const targetRef = useRef(null);
  const userId = String(user?._id || "");
  const cachedAvatar = avatarCache.get(userId);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(cachedAvatar?.expiresAt > Date.now() ? cachedAvatar.url : "");

  useEffect(() => {
    const cached = avatarCache.get(userId);
    setAvatarUrl(cached?.expiresAt > Date.now() ? cached.url : "");
    setShouldLoad(false);
  }, [userId]);

  useEffect(() => {
    if (!userId || !token) return undefined;
    const target = targetRef.current;
    if (!target || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, { rootMargin: "160px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [userId, token]);

  useEffect(() => {
    if (!shouldLoad || !userId || !token || avatarUrl) return undefined;
    let active = true;
    requestJson(`/user/avatar/${encodeURIComponent(userId)}`, token)
      .then((data) => {
        const url = typeof data?.avatarUrl === "string" ? data.avatarUrl : "";
        if (!url) return;
        avatarCache.set(userId, { url, expiresAt: Date.now() + AVATAR_CACHE_MS });
        if (active) setAvatarUrl(url);
      })
      .catch(() => {
        // An avatar is decorative. Keep initials if this individual image fails.
      });
    return () => { active = false; };
  }, [avatarUrl, shouldLoad, token, userId]);

  return (
    <Box ref={targetRef} sx={{ width: size, height: size, flex: "0 0 auto" }}>
      <Avatar
        src={avatarUrl}
        imgProps={{ loading: "lazy" }}
        onError={() => {
          setAvatarUrl("");
          setShouldLoad(false);
        }}
        sx={{ width: size, height: size, bgcolor: "#dbeafe", color: "#2563eb", fontWeight: 800 }}
      >
        {user?.name?.charAt(0)?.toUpperCase() || "U"}
      </Avatar>
    </Box>
  );
};

const RemoveUser = () => {
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [users, setUsers] = useState([]); // State for users data
  const [filteredUsers, setFilteredUsers] = useState([]); // State for filtered users
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userRole, setUserRole] = useState("");
  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [Token, setToken] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [usersLoadError, setUsersLoadError] = useState("");

  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const currentRole = String(JSON.parse(localStorage.getItem("userSession"))?.user_role || "").trim().toLowerCase();
  const canGrantMeetingEdit = ["dev", "srdev", "sr dev"].includes(currentRole);

  const loadUsers = useCallback(async (sessionToken) => {
    if (!sessionToken) {
      setUsers([]);
      setUsersLoadError("Your session is missing. Please sign in again.");
      setIsLoadingUsers(false);
      return;
    }

    setIsLoadingUsers(true);
    setUsersLoadError("");
    try {
      const data = await requestJson('/user/all', sessionToken);
      setUsers(Array.isArray(data?.Users) ? data.Users : []);
    } catch (error) {
      console.error("Error fetching users", error);
      setUsersLoadError(error.message || "Users could not be loaded. Please retry.");
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    const userSession = JSON.parse(localStorage.getItem("userSession"));
    const sessionToken = userSession?.token || "";
    setToken(sessionToken);
    loadUsers(sessionToken);
  }, [loadUsers]);

  useEffect(() => {
    // Filter users based on search term
    const filtered = users.filter((user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]); // Re-run filter whenever searchTerm or users change

  const handleEditClick = (user) => {
    setIsEditing(true);
    setSelectedUser(user);
    setName(user.name?.toUpperCase() || "");
    setEmail(user.email?.toLowerCase() || "");
    setUserRole(user.user_role);
    setPassword("");
    setFeaturePermissions(Array.isArray(user.feature_permissions) ? user.feature_permissions : getDefaultFeaturePermissionsForRole(user.user_role));
  };

  const handleEditRoleChange = (value) => {
    setUserRole(value);
    setFeaturePermissions(getDefaultFeaturePermissionsForRole(value));
  };

  const applyEditPresetRole = (roleKey) => {
    const preset = applyRoleTemplate(roleKey);
    setUserRole(preset.role);
    setFeaturePermissions(preset.permissions);
  };

  const toggleFeaturePermission = (featureKey) => {
    setFeaturePermissions((prev) =>
      prev.includes(featureKey)
        ? prev.filter((key) => key !== featureKey)
        : [...prev, featureKey]
    );
  };

  const handleSave = async () => {
    try {
      const body = { name: name.toUpperCase(), email: email.toLowerCase(), user_role: userRole, feature_permissions: featurePermissions };
      if (password.trim()) body.password = password;

      await axios.patch(
        `${apiUrl}/user/edituser/${selectedUser._id}`,
        body,
        {
          headers: {
            Authorization: `${Token}`,
            "Content-Type": "application/json",
          },
        }
      );

      await loadUsers(Token);
      enqueueSnackbar("User Updated successfully!", { variant: "success" });
      setIsEditing(false);
      setSelectedUser(null);
    } catch (error) {
      enqueueSnackbar(`${error.response?.data?.message || 'Error updating user'}`, { variant: "error" });
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const shouldDisable = !userToDelete.isDisabled;
      const response = await axios.patch(`${apiUrl}/user/account-status/${userToDelete._id}`, {
        disabled: shouldDisable,
      }, {
        headers: {
          Authorization: `${Token}`,
          "Content-Type": "application/json",
        },
      });

      setUsers((previousUsers) => previousUsers.map((user) =>
        String(user._id) === String(userToDelete._id) ? response.data.user : user
      ));
      enqueueSnackbar(shouldDisable ? "User disabled. Historical data remains intact." : "User enabled successfully.", { variant: "success" });
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      enqueueSnackbar(`${error.response?.data?.message || 'Error changing user status'}`, { variant: "error" });
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, width: "100%", maxWidth: 1560, mx: "auto", overflowX: "hidden" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.6rem", sm: "2rem" }, fontWeight: 800 }}>
          User Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add users, assign tab permissions, and manage existing team access from one workspace.
        </Typography>
      </Box>

      <AddUser />
      <Paper
        elevation={0}
        sx={{
          mt: 2.2,
          p: { xs: 1.2, sm: 1.5, md: 1.8 },
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: theme.palette.mode === "dark" ? "0 12px 28px rgba(2,6,23,0.28)" : "0 14px 34px rgba(15,23,42,0.05)",
          background: theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)"
            : "linear-gradient(180deg, rgba(247,251,255,0.95) 0%, #ffffff 100%)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Users List</Typography>
            <Typography variant="body2" color="text.secondary">Search and manage every CRM user from here.</Typography>
          </Box>
          <TextField
            label="Search Users"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: { xs: "100%", sm: 320 } }}
            size="small"
          />
        </Box>

      {usersLoadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => loadUsers(Token)}>Retry</Button>}
        >
          {usersLoadError}
        </Alert>
      )}

      {isLoadingUsers ? (
        <Box sx={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25 }}>
          <CircularProgress size={26} />
          <Typography color="text.secondary">Loading users…</Typography>
        </Box>
      ) : isMobileOrTablet ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
          {filteredUsers.length > 0 ? filteredUsers.map((user, index) => (
            <Paper key={user._id} sx={{ p: 1.4, borderRadius: "8px", border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                <Stack direction="row" spacing={1.1} sx={{ minWidth: 0, flex: 1 }}>
                  <UserAvatar user={user} token={Token} size={42} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>#{index + 1}</Typography>
                    <Typography sx={{ fontWeight: 800, mt: 0.3 }}>{user.name?.toUpperCase()}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, wordBreak: "break-word" }}>{user.email?.toLowerCase()}</Typography>
                  </Box>
                </Stack>
                <Chip
                  size="small"
                  label={user.user_role}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(59,130,246,0.10)",
                    color: theme.palette.mode === "dark" ? "#e2e8f0" : "#2563eb",
                  }}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
                <IconButton
                  sx={{
                    backgroundColor: "#111827",
                    color: "#fff",
                    '&:hover': { backgroundColor: "#000000" },
                  }}
                  onClick={() => handleEditClick(user)}
                  size="small"
                >
                  <Edit />
                </IconButton>
                <IconButton
                  color={user.isDisabled ? "success" : "warning"}
                  onClick={() => handleDeleteClick(user)}
                  size="small"
                  aria-label={user.isDisabled ? "Enable user" : "Disable user"}
                >
                  {user.isDisabled ? <PersonAdd /> : <PersonOff />}
                </IconButton>
              </Box>
            </Paper>
          )) : (
            <Paper sx={{ p: 2, borderRadius: "8px", textAlign: "center" }}>No users found.</Paper>
          )}
        </Box>
      ) : (
      <TableContainer component={Paper} sx={{ overflowX: "auto", borderRadius: "8px", border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
        <Table sx={{ minWidth: 580 }}>
          <TableHead>
            <TableRow>
              <TableCell>No</TableCell>
              <TableCell>Name</TableCell>
              {!isMobileOrTablet && <TableCell>Email</TableCell>}
              <TableCell>Role</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <TableRow key={user._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.1} alignItems="center">
                      <UserAvatar user={user} token={Token} />
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>{user.name?.toUpperCase()}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  {!isMobileOrTablet && <TableCell>{user.email?.toLowerCase()}</TableCell>}
                  <TableCell>
                    <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={user.user_role}
                        sx={{
                          borderRadius: "8px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(59,130,246,0.10)",
                          color: theme.palette.mode === "dark" ? "#e2e8f0" : "#2563eb",
                        }}
                      />
                      {user.isDisabled && <Chip size="small" label="Disabled" color="warning" sx={{ fontWeight: 700 }} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      {isMobileOrTablet ? (
                        <>
                          <IconButton
                            sx={{
                              backgroundColor: "#111827",
                              color: "#fff",
                              '&:hover': { backgroundColor: "#000000" },
                            }}
                            onClick={() => handleEditClick(user)}
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color={user.isDisabled ? "success" : "warning"}
                            onClick={() => handleDeleteClick(user)}
                            size="small"
                            aria-label={user.isDisabled ? "Enable user" : "Disable user"}
                          >
                            {user.isDisabled ? <PersonAdd /> : <PersonOff />}
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleEditClick(user)}
                            sx={{
                              mr: 1,
                              backgroundColor: "#111827",
                              '&:hover': { backgroundColor: "#000000" },
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            color={user.isDisabled ? "success" : "warning"}
                            size="small"
                            onClick={() => handleDeleteClick(user)}
                          >
                            {user.isDisabled ? "Enable" : "Disable"}
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      </Paper>

      <Dialog open={isEditing} onClose={() => setIsEditing(false)} fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value.toUpperCase())}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Role"
                fullWidth
                value={userRole}
                onChange={(e) => handleEditRoleChange(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {ROLE_TEMPLATE_OPTIONS.map((template) => {
                  const isActive = userRole.toLowerCase() === template.key;
                  return (
                    <Button
                      key={template.key}
                      type="button"
                      size="small"
                      variant={isActive ? "contained" : "outlined"}
                      onClick={() => applyEditPresetRole(template.key)}
                      sx={{
                        borderRadius: "999px",
                        px: 1.4,
                        py: 0.45,
                        fontSize: "0.72rem",
                        minWidth: 0,
                        ...(isActive
                          ? {
                            backgroundColor: "#ff3b1f",
                            '&:hover': { backgroundColor: "#e03118" },
                          }
                          : {
                            borderColor: "rgba(148,163,184,0.5)",
                            color: "text.secondary",
                          }),
                      }}
                    >
                      {template.label}
                    </Button>
                  );
                })}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                  maxHeight: 230,
                  overflowY: "auto",
                  backgroundColor: "background.default",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Tab permissions</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                  {FEATURE_KEYS.filter((featureKey) => canGrantMeetingEdit || featureKey !== "meeting_updates_edit").map((featureKey) => (
                    <label
                      key={featureKey}
                      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}
                    >
                      <input
                        type="checkbox"
                        checked={featurePermissions.includes(featureKey)}
                        onChange={() => toggleFeaturePermission(featureKey)}
                      />
                      {FEATURE_LABELS[featureKey] || featureKey}
                    </label>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
          <Button
            onClick={() => {
              setIsEditing(false);
              setSelectedUser(null);
            }}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Account status confirmation modal */}
      <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
        <DialogTitle>{userToDelete?.isDisabled ? "Enable User" : "Disable User"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {userToDelete?.isDisabled
              ? `Enable ${userToDelete?.name || "this user"} and restore login access?`
              : `Disable ${userToDelete?.name || "this user"}? They will be signed out immediately and cannot log in, while all bookings and historical data remain intact.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmDelete} color={userToDelete?.isDisabled ? "success" : "warning"} variant="contained">
            {userToDelete?.isDisabled ? "Enable" : "Disable"}
          </Button>
          <Button
            onClick={closeDeleteModal}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemoveUser;
